'use client';

import { useState } from 'react';
import { Phone } from 'lucide-react';
import type { EmergencyContact, Person } from '@/lib/types';
import { formatPhone, phoneOk } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { CARD, CTA_SMALL, FIELD, Kicker, PageBody } from './ui';

/**
 * The emergency contact, the football way: the household is offered as
 * one-tap links before any text boxes, because a linked contact survives
 * the person changing their number and is the only version of the answer
 * that can wear a face. "Someone else…" is the typed fallback: name and
 * phone, no photo, because an avatar invented for a name we cannot resolve
 * would be a claim.
 */
/**
 * How an emergency contact reads on a row: a face and "First Last" for a
 * person we know, a phone glyph for somebody typed in. Never MP's
 * "Last, First".
 */
export function EmergencyValue({ ec }: { ec: EmergencyContact }) {
  const { data } = useProfile();
  const linked = ec.contactId
    ? [data.me, ...data.members].find((p) => p.contactId === ec.contactId)
    : null;
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 align-middle">
      {ec.kind === 'person' ? (
        <Avatar
          name={linked?.displayName ?? ec.name}
          imageUrl={linked?.imageUrl ?? ec.imageUrl ?? null}
          size={20}
        />
      ) : (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          <Phone className="h-3 w-3" strokeWidth={2.5} />
        </span>
      )}
      <span className="pii truncate">{linked?.displayName ?? ec.name}</span>
    </span>
  );
}

/** `onDone` closes the sheet: this is its first page now, so there is nothing to go back to. */
export function EmergencyPage({ person, onDone }: { person: Person; onDone: () => void }) {
  const { data, api, run, update } = useProfile();
  const [someoneElse, setSomeoneElse] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const household = [data.me, ...data.members].filter(
    (p) => p.contactId !== person.contactId && !p.isChild
  );
  const current = person.emergencyContact;
  const linked = current?.contactId
    ? [data.me, ...data.members].find((p) => p.contactId === current.contactId)
    : null;

  const write = (
    next: EmergencyContact | null,
    input: Parameters<typeof api.setEmergencyContact>[1]
  ) => {
    const snapshot = data;
    const apply = <P extends Person>(p: P): P =>
      p.contactId === person.contactId ? { ...p, emergencyContact: next } : p;
    update((d) => ({ ...d, me: apply(d.me), members: d.members.map(apply) }));
    onDone();
    void run(
      async () => {
        const saved = await api.setEmergencyContact(person.contactId, input);
        update((d) => {
          const fix = <P extends Person>(p: P): P =>
            p.contactId === person.contactId ? { ...p, emergencyContact: saved } : p;
          return { ...d, me: fix(d.me), members: d.members.map(fix) };
        });
      },
      () => update(() => snapshot)
    );
  };

  const pickHousehold = (p: Person) =>
    write(
      {
        kind: 'person',
        contactId: p.contactId,
        name: p.displayName,
        phone: p.mobilePhone,
        imageGuid: null,
        imageUrl: p.imageUrl,
      },
      { relatedContactId: p.contactId }
    );
  const saveTyped = () =>
    write(
      { kind: 'external', contactId: null, name: name.trim(), phone, imageGuid: null },
      { name: name.trim(), phone }
    );
  const remove = () => write(null, { relatedContactId: null });

  return (
    <PageBody>
      {current && (
        <div className="flex flex-col gap-2">
          <Kicker>Right now</Kicker>
          <div className={`${CARD} flex items-center gap-3 p-4`}>
            {current.kind === 'person' ? (
              <Avatar
                name={current.name}
                imageUrl={linked?.imageUrl ?? current.imageUrl ?? null}
                size={40}
              />
            ) : (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <Phone className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-[#1c2b39]">
                {current.name}
              </span>
              <span className="block truncate text-xs text-gray-500">
                {[current.phone, linked?.relationship?.label].filter(Boolean).join(' · ') ||
                  'Emergency contact'}
              </span>
            </span>
            <button
              type="button"
              onClick={remove}
              className="shrink-0 text-xs font-semibold text-gray-400 hover:text-red-600"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {household.length > 0 && (
        <div className="flex flex-col gap-2">
          <Kicker>{current ? 'Change to' : 'In your household'}</Kicker>
          <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
            {household.map((p) => {
              const active = current?.contactId === p.contactId;
              return (
                <button
                  key={p.contactId}
                  type="button"
                  disabled={active}
                  onClick={() => pickHousehold(p)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
                >
                  <Avatar name={p.displayName} imageUrl={p.imageUrl} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                      {p.displayName}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {[
                        p.mobilePhone,
                        p.contactId === data.me.contactId ? 'You' : p.relationship?.label,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'No mobile on file'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {someoneElse ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && phoneOk(phone)) saveTyped();
          }}
          className={`${CARD} flex flex-col gap-3 p-4`}
        >
          <Kicker>Someone else</Kicker>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Their name"
              className={FIELD}
              maxLength={100}
            />
            <input
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              inputMode="tel"
              placeholder="Mobile"
              className={FIELD}
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!name.trim() || !phoneOk(phone)}
              className={`${CTA_SMALL} bg-[#299c8f] text-white`}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setSomeoneElse(false)}
              className="text-sm font-semibold text-gray-500 hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setSomeoneElse(true)}
          className="self-start text-sm font-semibold text-[#299c8f] hover:underline"
        >
          Someone else…
        </button>
      )}
    </PageBody>
  );
}
